import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutContent } from '@/mocks/articles';

export default function AboutPage() {
  const { t } = useTranslation();
  const { brandName, tagline, introduction, mission, vision, reputation, contact, faq } = aboutContent;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: brandName,
        url: 'https://mangast.github.io',
        description: tagline,
        contactPoint: {
          '@type': 'ContactPoint',
          email: contact.email,
          contactType: 'customer service',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: contact.address,
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'WebPage',
        name: t('seo.about.title'),
        description: t('seo.about.description'),
        url: 'https://mangast.github.io/about',
        isPartOf: { '@id': 'https://mangast.github.io/#website' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  return (
    <SEO
      title={t('seo.about.title')}
      description={t('seo.about.description')}
      keywords={t('seo.about.keywords')}
      canonicalUrl="https://mangast.github.io/about"
    >
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: t('article.breadcrumb.home'), href: '/' },
                  { label: t('about.title') },
                ]}
              />
            </div>

            <section className="text-center mb-16 py-12 bg-background-100 rounded-lg">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {brandName}
              </h1>
              <p className="text-lg text-foreground-600">{tagline}</p>
            </section>

            <section className="mb-16">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('about.intro')}
              </h2>
              <div
                className="text-foreground-700 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: introduction }}
              />
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-flag-line text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-950 mb-3">{t('about.mission')}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{mission}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-eye-line text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-950 mb-3">{t('about.vision')}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{vision}</p>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('about.reputation')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {reputation.map((item) => (
                  <div key={item.label} className="bg-background-100 rounded-lg p-6 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div className="text-2xl font-bold text-foreground-950 mb-1">{item.metric}</div>
                    <div className="text-xs text-foreground-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16" id="contact">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('about.contact')}
              </h2>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">{t('about.contact.email')}</div>
                      <div className="text-sm font-medium text-foreground-900">{contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-weibo-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">{t('about.contact.weibo')}</div>
                      <div className="text-sm font-medium text-foreground-900">{contact.weibo}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-bilibili-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">{t('about.contact.bilibili')}</div>
                      <div className="text-sm font-medium text-foreground-900">{contact.bilibili}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">{t('about.contact.wechat')}</div>
                      <div className="text-sm font-medium text-foreground-900">{contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:col-span-2 lg:col-span-2">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">{t('about.contact.address')}</div>
                      <div className="text-sm font-medium text-foreground-900">{contact.address}</div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('about.faq')}
              </h2>
              <div className="space-y-4">
                {faq.map((item, index) => (
                  <details key={index} className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between p-5 font-medium text-foreground-900 list-none">
                      <span className="text-sm">{item.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </SEO>
  );
}